import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Search,
  Filter,
  Download,
  Eye,
  Package,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Truck,
  Calendar,
  User,
  CreditCard,
  MapPin,
  Phone,
  Mail,
  ExternalLink,
  FileText,
  RotateCcw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import adminService from "@/services/adminService";
import { formatCurrency, formatDate } from "@/lib/utils";

interface OrderItem {
  product: string;
  title: string;
  type: "físico" | "digital" | "taller";
  price: number;
  quantity: number;
  subtotal: number;
}

interface PaymentInfo {
  provider: string;
  transactionId?: string;
  status: "pending" | "processing" | "completed" | "failed" | "refunded";
  paidAt?: string;
  paymentMethod?: string;
  lastFourDigits?: string;
}

interface ShippingAddress {
  fullName?: string;
  email?: string;
  phone?: string;
  street?: string;
  district?: string;
  corregimiento?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  notes?: string;
}

interface StatusHistory {
  status: string;
  changedAt?: string;
  notes?: string;
}

interface Order {
  _id: string;
  orderNumber: string;
  isGuest: boolean;
  guestInfo?: {
    fullName: string;
    email: string;
    phone?: string;
  };
  user?: {
    _id: string;
    name: string;
    email: string;
  };
  items: OrderItem[];
  totalItems: number;
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  status: "pending" | "processing" | "shipped" | "delivered" | "cancelled";
  paymentInfo: PaymentInfo;
  shippingAddress?: ShippingAddress;
  statusHistory: StatusHistory[];
  physicalDelivery?: {
    firstConfirmationSent: boolean;
    firstConfirmationSentAt?: string;
    secondConfirmationSent: boolean;
    secondConfirmationSentAt?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface OrderFilters {
  search?: string;
  status?: string;
  paymentStatus?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

const statusColors = {
  pending: "warning",
  processing: "default",
  shipped: "secondary",
  delivered: "success",
  cancelled: "destructive",
} as const;

const paymentStatusColors = {
  pending: "warning",
  processing: "default",
  completed: "success",
  failed: "destructive",
  refunded: "secondary",
} as const;

const StatusBadge = ({
  status,
  type = "order",
}: {
  status: string;
  type?: "order" | "payment";
}) => {
  const colors = type === "order" ? statusColors : paymentStatusColors;
  const color = colors[status as keyof typeof colors] || "default";

  return (
    <Badge variant={color}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

const OrderDetails = ({ order }: { order: Order }) => {
  const totalPhysicalItems = order.items.filter(
    (item) => item.type === "físico"
  ).length;
  const totalDigitalItems = order.items.filter(
    (item) => item.type === "digital"
  ).length;

  return (
    <div className="space-y-6">
      {/* Información General */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Package className="h-4 w-4" />
              Información del Pedido
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Número:</span>
              <span className="font-mono">{order.orderNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Estado:</span>
              <StatusBadge status={order.status} />
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total:</span>
              <span className="font-medium">{formatCurrency(order.total)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Fecha:</span>
              <span>{formatDate(order.createdAt)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Nombre:</span>
              <span>
                {order.isGuest
                  ? order.guestInfo?.fullName
                  : order.user?.name || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email:</span>
              <span className="text-xs">
                {order.isGuest
                  ? order.guestInfo?.email
                  : order.user?.email || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Teléfono:</span>
              <span>
                {order.shippingAddress?.phone ||
                  order.guestInfo?.phone ||
                  "N/A"}
              </span>
            </div>
            {order.isGuest && (
              <div className="pt-2 border-t">
                <Badge variant="outline" className="text-xs">
                  Usuario Invitado
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Pago
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Estado:</span>
              <StatusBadge status={order.paymentInfo.status} type="payment" />
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Método:</span>
              <span>{order.paymentInfo.paymentMethod || "N/A"}</span>
            </div>
            {order.paymentInfo.transactionId && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">ID Trans:</span>
                <span className="font-mono text-xs">
                  {order.paymentInfo.transactionId}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="items" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="items">
            Productos ({order.totalItems})
          </TabsTrigger>
          <TabsTrigger value="shipping">Envío</TabsTrigger>
          <TabsTrigger value="history">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Productos del Pedido</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {order.items.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.title}</span>
                        <Badge
                          variant={
                            item.type === "físico" ? "default" : "secondary"
                          }
                        >
                          {item.type}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatCurrency(item.price)} × {item.quantity}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">
                        {formatCurrency(item.subtotal)}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="border-t pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(order.subtotal)}</span>
                  </div>
                  {order.tax > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Impuestos:</span>
                      <span>{formatCurrency(order.tax)}</span>
                    </div>
                  )}
                  {order.discount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Descuento:</span>
                      <span>-{formatCurrency(order.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-medium text-lg border-t pt-2">
                    <span>Total:</span>
                    <span>{formatCurrency(order.total)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="shipping" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Información de Envío
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Destinatario</h4>
                  <div className="space-y-1 text-sm">
                    <div>
                      {order.shippingAddress?.fullName ||
                        order.guestInfo?.fullName ||
                        "N/A"}
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-3 w-3" />
                      {order.shippingAddress?.email ||
                        order.guestInfo?.email ||
                        "N/A"}
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-3 w-3" />
                      {order.shippingAddress?.phone ||
                        order.guestInfo?.phone ||
                        "N/A"}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Dirección</h4>
                  <div className="space-y-1 text-sm">
                    <div>{order.shippingAddress?.street || "N/A"}</div>
                    <div>
                      {order.shippingAddress?.district &&
                        `${order.shippingAddress.district}, `}
                      {order.shippingAddress?.corregimiento &&
                        `${order.shippingAddress.corregimiento}, `}
                      {order.shippingAddress?.province || "N/A"}
                    </div>
                    <div>{order.shippingAddress?.postalCode || "N/A"}</div>
                    <div>{order.shippingAddress?.country || "Panamá"}</div>
                  </div>
                </div>
              </div>

              {order.shippingAddress?.notes && (
                <div>
                  <h4 className="font-medium mb-2">Notas de Entrega</h4>
                  <p className="text-sm bg-gray-50 p-3 rounded-lg">
                    {order.shippingAddress.notes}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-4 pt-3 border-t">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">
                    {totalPhysicalItems} productos físicos
                  </span>
                </div>
                {totalDigitalItems > 0 && (
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-green-500" />
                    <span className="text-sm">
                      {totalDigitalItems} productos digitales
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Historial de Estados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {order.statusHistory.map((history, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 border rounded-lg"
                  >
                    <div className="mt-1">
                      <StatusBadge status={history.status} />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        Estado cambiado a: {history.status}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(history.changedAt)}
                      </div>
                      {history.notes && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {history.notes}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const StatusUpdateForm = ({
  order,
  onUpdate,
  isLoading,
}: {
  order: Order;
  onUpdate: (status: string, notes?: string) => void;
  isLoading: boolean;
}) => {
  const [newStatus, setNewStatus] = useState(order.status);
  const [notes, setNotes] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newStatus !== order.status) {
      onUpdate(newStatus, notes);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">Nuevo Estado</label>
        <Select value={newStatus} onValueChange={setNewStatus}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pendiente</SelectItem>
            <SelectItem value="processing">Procesando</SelectItem>
            <SelectItem value="shipped">Enviado</SelectItem>
            <SelectItem value="delivered">Entregado</SelectItem>
            <SelectItem value="cancelled">Cancelado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          Notas (opcional)
        </label>
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Agregar notas sobre el cambio de estado..."
        />
      </div>

      <Button
        type="submit"
        disabled={isLoading || newStatus === order.status}
        className="w-full"
      >
        {isLoading ? "Actualizando..." : "Actualizar Estado"}
      </Button>
    </form>
  );
};

const AdminOrders = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState<OrderFilters>({
    search: searchParams.get("search") || "",
    status: "",
    paymentStatus: "",
    startDate: "",
    endDate: "",
    page: 1,
    limit: 10,
  });

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [viewFilter, setViewFilter] = useState<"all" | "talleres">("all");

  // Query para obtener órdenes
  const { data: ordersData, isLoading } = useQuery({
    queryKey: ["admin-orders", filters],
    queryFn: () => adminService.getAllOrders(filters),
    staleTime: 30 * 1000, // 30 segundos
  });

  // Mutación para actualizar estado de orden
  const updateStatusMutation = useMutation({
    mutationFn: ({
      orderId,
      status,
      notes,
    }: {
      orderId: string;
      status: string;
      notes?: string;
    }) => adminService.updateOrderStatus(orderId, status, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-orders"] });
      setIsUpdateOpen(false);
      setSelectedOrder(null);
      toast({
        title: "Estado actualizado",
        description: "El estado de la orden se ha actualizado exitosamente",
      });
    },
    onError: (error: unknown) => {
      let errorMessage = "Error al actualizar el estado";
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "message" in error.response.data
      ) {
        errorMessage = String(error.response.data.message);
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Mutación para marcar orden como pagada
  const markAsPaidMutation = useMutation({
    mutationFn: (orderId: string) => adminService.markOrderAsPaid(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-orders"] });
      setIsDetailsOpen(false);
      setSelectedOrder(null);
      toast({
        title: "Orden marcada como pagada",
        description:
          "Se enviarán los emails de confirmación automáticamente según el tipo de producto",
      });
    },
    onError: (error: unknown) => {
      let errorMessage = "Error al marcar la orden como pagada";
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "message" in error.response.data
      ) {
        errorMessage = String(error.response.data.message);
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Mutación para enviar coordinación de entrega
  const sendDeliveryCoordinationMutation = useMutation({
    mutationFn: (orderId: string) =>
      adminService.sendDeliveryCoordination(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-orders"] });
      setIsDetailsOpen(false);
      setSelectedOrder(null);
      toast({
        title: "Email de coordinación enviado",
        description:
          "Se ha enviado el email al cliente para coordinar la entrega del producto físico",
      });
    },
    onError: (error: unknown) => {
      let errorMessage = "Error al enviar el email de coordinación";
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "message" in error.response.data
      ) {
        errorMessage = String(error.response.data.message);
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const handleStatusUpdate = (status: string, notes?: string) => {
    if (selectedOrder) {
      updateStatusMutation.mutate({
        orderId: selectedOrder._id,
        status,
        notes,
      });
    }
  };

  const handleMarkAsPaid = (order: Order) => {
    if (
      confirm(
        `¿Confirmar que la orden ${order.orderNumber} ha sido pagada?\n\nEsto enviará emails automáticos al cliente según el tipo de producto.`
      )
    ) {
      markAsPaidMutation.mutate(order._id);
    }
  };

  const handleSendDeliveryCoordination = (order: Order) => {
    if (
      confirm(
        `¿Enviar email de coordinación de entrega para la orden ${order.orderNumber}?\n\nEsto enviará un email al cliente solicitándole que coordine la entrega del producto físico.`
      )
    ) {
      sendDeliveryCoordinationMutation.mutate(order._id);
    }
  };

  const openDetails = (order: Order) => {
    setSelectedOrder(order);
    setIsDetailsOpen(true);
  };

  const openStatusUpdate = (order: Order) => {
    setSelectedOrder(order);
    setIsUpdateOpen(true);
  };

  const exportOrders = async () => {
    try {
      await adminService.exportOrders(filters);
      toast({
        title: "Exportación iniciada",
        description: "Se descargará el archivo CSV con las órdenes",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Error al exportar las órdenes",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allOrders = ordersData?.orders || [];
  const orders = viewFilter === "talleres"
    ? allOrders.filter((o) => o.items.some((i) => i.type === "taller"))
    : allOrders;
  const totalOrders = orders.length;
  const pendingOrders = orders.filter((o) => o.status === "pending").length;
  const processingOrders = orders.filter(
    (o) => o.status === "processing"
  ).length;
  const completedOrders = orders.filter((o) => o.status === "delivered").length;
  const totalRevenue = orders.reduce((acc, order) => acc + order.total, 0);

  function sendPaidProduct() {}

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Gestión de Órdenes
          </h1>
          <p className="text-muted-foreground">
            Administra todas las órdenes y su estado de entrega
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewFilter === "talleres" ? "default" : "outline"}
            className={`flex items-center gap-2 ${viewFilter === "talleres" ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}`}
            onClick={() => setViewFilter(viewFilter === "talleres" ? "all" : "talleres")}
          >
            🎨 {viewFilter === "talleres" ? "Viendo Talleres" : "Solo Talleres"}
          </Button>
          <Button
            onClick={exportOrders}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Exportar CSV
          </Button>
        </div>
      </div>
      <div>
        <Input
          placeholder="email del cliente que pago"
          type="email"
          className="pl-10"
        />
        <Button
          onClick={sendPaidProduct}
          variant="outline"
          className="flex items-center gap-2"
        >
          Enviar Producto Pagado
        </Button>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Órdenes</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalOrders}</div>
            <p className="text-xs text-muted-foreground">
              {completedOrders} completadas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pendientes</CardTitle>
            <AlertCircle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingOrders}</div>
            <p className="text-xs text-muted-foreground">Requieren atención</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Procesando</CardTitle>
            <Clock className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{processingOrders}</div>
            <p className="text-xs text-muted-foreground">En preparación</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Facturación</CardTitle>
            <DollarSign className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(totalRevenue)}
            </div>
            <p className="text-xs text-muted-foreground">Total en órdenes</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Buscar por número, cliente..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: e.target.value,
                      page: 1,
                    }))
                  }
                  className="pl-10"
                />
              </div>
            </div>

            <Select
              value={filters.status || "all"}
              onValueChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  status: value === "all" ? "" : value,
                  page: 1,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="processing">Procesando</SelectItem>
                <SelectItem value="shipped">Enviado</SelectItem>
                <SelectItem value="delivered">Entregado</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.paymentStatus || "all"}
              onValueChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  paymentStatus: value === "all" ? "" : value,
                  page: 1,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Pago" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los pagos</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="processing">Procesando</SelectItem>
                <SelectItem value="completed">Completado</SelectItem>
                <SelectItem value="failed">Fallido</SelectItem>
                <SelectItem value="refunded">Reembolsado</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  startDate: e.target.value,
                  page: 1,
                }))
              }
              placeholder="Fecha inicio"
            />

            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  endDate: e.target.value,
                  page: 1,
                }))
              }
              placeholder="Fecha fin"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabla de órdenes */}
      <Card>
        <CardHeader>
          <CardTitle>Órdenes ({totalOrders})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Orden</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Pago</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => {
                // Debug temporal - puedes borrar esto después
                const hasPhysical = order.items.some(
                  (item) => item.type === "físico" || item.type === "físico"
                );
                const isPaid = order.paymentInfo.status === "completed";
                const notSent = !order.physicalDelivery?.secondConfirmationSent;
                if (isPaid && hasPhysical) {
                  console.log("Orden con físicos pagada:", {
                    orderNumber: order.orderNumber,
                    hasPhysical,
                    isPaid,
                    notSent,
                    physicalDelivery: order.physicalDelivery,
                    itemTypes: order.items.map((i) => i.type),
                  });
                }

                return (
                  <TableRow key={order._id}>
                    <TableCell>
                      <div>
                        <div className="font-mono font-medium">
                          {order.orderNumber}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          {order.totalItems} producto{order.totalItems !== 1 ? "s" : ""}
                          {order.items.some((i) => i.type === "taller") && (
                            <span className="ml-1 text-xs bg-amber-100 text-amber-700 font-semibold px-1.5 py-0.5 rounded">🎨 Taller</span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {order.isGuest
                            ? order.guestInfo?.fullName
                            : order.user?.name || "N/A"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {order.isGuest
                            ? order.guestInfo?.email
                            : order.user?.email || "N/A"}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={order.status} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        status={order.paymentInfo.status}
                        type="payment"
                      />
                    </TableCell>
                    <TableCell>{formatCurrency(order.total)}</TableCell>
                    <TableCell>{formatDate(order.createdAt)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetails(order)}
                          title="Ver detalles"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openStatusUpdate(order)}
                          title="Actualizar estado"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                        {order.paymentInfo.status !== "completed" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkAsPaid(order)}
                            disabled={markAsPaidMutation.isPending}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Marcar como pagada"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                        {/* Botón de coordinación de entrega: solo para órdenes pagadas con productos físicos */}
                        {order.paymentInfo.status === "completed" &&
                          order.items.some(
                            (item) =>
                              item.type === "físico" || item.type === "físico"
                          ) &&
                          !order.physicalDelivery?.secondConfirmationSent && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleSendDeliveryCoordination(order)
                              }
                              disabled={
                                sendDeliveryCoordinationMutation.isPending
                              }
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Enviar coordinación de entrega"
                            >
                              <Truck className="h-4 w-4" />
                            </Button>
                          )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal de detalles */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Detalles de la Orden {selectedOrder?.orderNumber}
            </DialogTitle>
            <DialogDescription>
              Información completa del pedido y su estado actual
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && <OrderDetails order={selectedOrder} />}
        </DialogContent>
      </Dialog>

      {/* Modal de actualización de estado */}
      <Dialog open={isUpdateOpen} onOpenChange={setIsUpdateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Actualizar Estado de Orden</DialogTitle>
            <DialogDescription>
              Cambiar el estado de la orden {selectedOrder?.orderNumber}
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <StatusUpdateForm
              order={selectedOrder}
              onUpdate={handleStatusUpdate}
              isLoading={updateStatusMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrders;
